import { DownloadOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Select, Table, Tag, message } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectUser } from "../../../../../redux/slices/auth.slice";

const { Option } = Select;

const FORM_TYPE_COLOR = {
  transfer_certificate: "blue",
  study_certificate: "green",
  conduct_certificate: "purple",
};

const CERT_TYPES = [
  { value: "transfer_certificate", label: "Transfer Certificate" },
  { value: "study_certificate", label: "Study Certificate" },
  { value: "conduct_certificate", label: "Conduct Certificate" },
];

const REQUEST_STATUS_COLOR = {
  pending: "gold",
  approved: "orange",
  completed: "green",
  rejected: "red",
};

const REQUEST_STATUS_LABEL = {
  pending: "Pending Approval",
  approved: "Processing",
  completed: "Ready to Download",
  rejected: "Rejected",
};

const ParentCertBuilderRequests = () => {
  const user = useSelector(selectUser);
  const queryClient = useQueryClient();

  const [certReqForm] = Form.useForm();
  const [certReqModal, setCertReqModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ─── Queries ─────────────────────────────────────────────────────────────────

  const students = useQuery("students/by-parent", () =>
    axios
      .get(`${serverUrl}/students/by-parent/`, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      })
      .then((r) => r.data.result),
  );

  const certRequests = useQuery("my-cert-requests", () =>
    axios
      .get(`${serverUrl}/cert-documents/my-requests`, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      })
      .then((r) => r.data),
  );

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  const handleDownload = (pdfUrl) => {
    window.open(pdfUrl, "_blank");
  };

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${serverUrl}/cert-documents/request`,
        {
          student: values.cert_student,
          form_type: values.cert_type,
          school: user?.school?._id,
          reason: values.cert_reason,
        },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      queryClient.invalidateQueries("my-cert-requests");
      certReqForm.resetFields();
      setCertReqModal(false);
      message.success("Certificate request submitted successfully");
    } catch (err) {
      const raw = err?.response?.data?.error?.errors?.[0];
      if (raw && raw.toLowerCase().includes("pending request")) {
        message.warning(
          "A pending request already exists for this student and certificate type. Please wait for the existing request to be processed.",
        );
      } else {
        message.error(raw || "Failed to submit request");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Table columns ────────────────────────────────────────────────────────────

  const columns = [
    {
      title: "Certificate Type",
      dataIndex: "form_type",
      key: "form_type",
      align: "center",
      render: (v) => (
        <Tag color={FORM_TYPE_COLOR[v] || "default"} style={{ textTransform: "capitalize" }}>
          {(v || "").replace(/_/g, " ")}
        </Tag>
      ),
    },
    {
      title: "Student",
      dataIndex: "student",
      key: "student",
      align: "center",
      render: (s) =>
        s ? `${s.first_name || ""} ${s.last_name || ""}`.trim() : "—",
    },
    {
      title: "Requested On",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "—"),
    },
    {
      title: "Status",
      dataIndex: "request_status",
      key: "request_status",
      align: "center",
      render: (v) => (
        <Tag color={REQUEST_STATUS_COLOR[v] || "default"}>
          {REQUEST_STATUS_LABEL[v] || v || "—"}
        </Tag>
      ),
    },
    {
      title: "Rejection Reason",
      dataIndex: "rejection_reason",
      key: "rejection_reason",
      align: "center",
      render: (v) =>
        v ? <span className="text-red-500 text-sm">{v}</span> : "—",
    },
    {
      title: "Download",
      key: "download",
      align: "center",
      render: (_, r) =>
        r?.request_status === "completed" && r?.pdf_url ? (
          <DownloadOutlined
            className="text-orange-500 text-lg cursor-pointer"
            onClick={() => handleDownload(r.pdf_url)}
          />
        ) : (
          "—"
        ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="p-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className="bg-white drop-shadow-md p-8 w-full"
        >
          <div className="flex justify-between items-center">
            <div className="text-2xl text-orange-500 font-semibold">
              Certificate Builder Requests
            </div>
            <div className="text-right pt-4">
              <Button
                type="primary"
                size="large"
                onClick={() => setCertReqModal(true)}
              >
                Request Certificate
              </Button>
            </div>
          </div>
          <div className="py-5">
            <Table
              rowKey="_id"
              columns={columns}
              dataSource={certRequests?.data?.result?.data || []}
              pagination={{ pageSize: 10 }}
              loading={certRequests?.isLoading}
            />
          </div>

          {/* Request modal */}
          <Modal
            width={460}
            visible={certReqModal}
            onCancel={() => {
              certReqForm.resetFields();
              setCertReqModal(false);
            }}
            footer={null}
            title={
              <span className="text-lg font-bold">Request Certificate</span>
            }
          >
            <Form
              form={certReqForm}
              layout="vertical"
              onFinish={onSubmit}
              autoComplete="off"
              className="pt-2"
            >
              <Form.Item
                label="Student"
                name="cert_student"
                rules={[{ required: true, message: "Please select a student" }]}
              >
                <Select
                  showSearch
                  showArrow
                  placeholder="Select student"
                  getPopupContainer={(t) => t.parentNode}
                >
                  {(students?.data || []).map((s) => (
                    <Option key={s._id} value={s._id}>
                      {`${s.first_name} ${s.last_name}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Certificate Type"
                name="cert_type"
                rules={[
                  {
                    required: true,
                    message: "Please select a certificate type",
                  },
                ]}
              >
                <Select
                  placeholder="Select type"
                  getPopupContainer={(t) => t.parentNode}
                >
                  {CERT_TYPES.map((t) => (
                    <Option key={t.value} value={t.value}>
                      {t.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Reason" name="cert_reason">
                <Input.TextArea
                  rows={3}
                  placeholder="Reason for requesting"
                  maxLength={200}
                />
              </Form.Item>

              <div className="text-center pt-2">
                <Button
                  size="large"
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  style={{
                    background: "#f97316",
                    borderColor: "#f97316",
                    paddingLeft: 32,
                    paddingRight: 32,
                  }}
                >
                  Submit Request
                </Button>
              </div>
            </Form>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default ParentCertBuilderRequests;
